import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle, XCircle, Loader2 } from "lucide-react";
import splashIcon from "../assets/images/splash-icon.png";
import splashIconDark from "../assets/images/ToronQ Logos-15.png";
import { verifyKybEmailTokenApi } from "../api/kyb.api";
import { useTheme } from "../context/ThemeContext";
import Button from "../components/ui/Button";

export default function KYBEmailConfirm() {
  const { theme, mode } = useTheme();
  const subtleBorder = mode === "dark" ? theme.border : "#E7EAF0";
  const { tokenParam } = useParams();

  const token = tokenParam?.startsWith("token=") ? tokenParam.slice(6) : tokenParam || null;

  const [status, setStatus] = useState("verifying");
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (!token) {
      setStatus("error");
      setErrorMessage("No verification token found in this link.");
      return;
    }

    // Endpoint is public — no auth required
    verifyKybEmailTokenApi(token)
      .then(() => setStatus("success"))
      .catch((err) => {
        const msg = err?.response?.data?.message || "";
        if (/already.verified|already.confirmed/i.test(msg)) {
          setStatus("success");
        } else {
          setStatus("error");
          setErrorMessage(msg || "This link may have expired or is invalid.");
        }
      });
  }, [token]);

  return (
    <div className="flex min-h-screen items-center justify-center px-4 py-8" style={{ background: theme.background }}>
      <div className="w-full max-w-md text-center">
        <img
          src={mode === "dark" ? splashIconDark : splashIcon}
          alt="ToronQ logo"
          className="mx-auto h-16 w-auto object-contain"
        />

        <div className="mt-8 rounded-2xl border p-8" style={{ borderColor: subtleBorder, background: theme.card }}>
          {status === "verifying" && (
            <>
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full" style={{ background: theme.infoBg }}>
                <Loader2 size={24} className="animate-spin" style={{ color: theme.primary }} />
              </div>
              <h1 className="mt-6 text-2xl font-semibold tracking-tight" style={{ color: theme.text }}>
                Verifying your email…
              </h1>
              <p className="mt-3 text-sm" style={{ color: theme.textMuted }}>
                Please wait while we confirm your company email.
              </p>
            </>
          )}

          {status === "success" && (
            <>
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full" style={{ background: theme.successBg }}>
                <CheckCircle size={24} style={{ color: theme.success }} />
              </div>
              <h1 className="mt-6 text-2xl font-semibold tracking-tight" style={{ color: theme.text }}>
                Email Verified!
              </h1>
              <p className="mt-3 text-sm leading-6" style={{ color: theme.textMuted }}>
                Your company email has been successfully verified.
              </p>
              <div className="mt-6 rounded-2xl border px-4 py-4 text-sm" style={{ borderColor: subtleBorder, background: theme.infoBg, color: theme.primary }}>
                <p className="font-semibold">Return to your previous tab</p>
                <p className="mt-1 text-xs" style={{ color: theme.textMuted }}>
                  Your verification page will automatically advance to the next step. You can safely close this tab.
                </p>
              </div>
              <Button variant="primary" theme={theme} onClick={() => window.close()} className="mt-6 w-full py-3">
                Close This Tab
              </Button>
            </>
          )}

          {status === "error" && (
            <>
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full" style={{ background: theme.dangerBg }}>
                <XCircle size={24} style={{ color: theme.danger }} />
              </div>
              <h1 className="mt-6 text-2xl font-semibold tracking-tight" style={{ color: theme.text }}>
                Verification Failed
              </h1>
              <p className="mt-3 text-sm leading-6" style={{ color: theme.textMuted }}>
                {errorMessage || "This link may have expired or is invalid. Please go back to the verification page and request a new email."}
              </p>
              <Button variant="secondary" theme={theme} onClick={() => window.close()} className="mt-6 w-full py-3">
                Close This Tab
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
